import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  baseX: number;
  baseY: number;
  size: number;
  color: string;
  opacity: number;
  pulsePhase: number;
  pulseSpeed: number;
}

interface PetMotif {
  x: number;
  y: number;
  rotation: number;
  scale: number;
  rotationSpeed: number;
  driftVx: number;
  driftVy: number;
  opacity: number;
  pulsePhase: number;
}

interface DataFlow {
  x: number;
  y: number;
  vx: number;
  vy: number;
  opacity: number;
  type: 'dog' | 'cat' | 'paw';
  scale: number;
}

export default function InteractiveBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const particlesRef = useRef<Particle[]>([]);
  const petMotifsRef = useRef<PetMotif[]>([]);
  const dataFlowsRef = useRef<DataFlow[]>([]);
  const animationFrameRef = useRef<number>();
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
      initPetMotifs();
      initDataFlows();
    };

    const initParticles = () => {
      particlesRef.current = [];
      const particleCount = 80;
      
      for (let i = 0; i < particleCount; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const color = Math.random() > 0.5 ? '#00D4FF' : '#7C3AED';
        const opacity = Math.random() * 0.08 + 0.04;
        
        particlesRef.current.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          baseX: x,
          baseY: y,
          size: Math.random() * 3 + 1.5,
          color,
          opacity,
          pulsePhase: Math.random() * Math.PI * 2,
          pulseSpeed: Math.random() * 0.02 + 0.01
        });
      }
    };

    const initPetMotifs = () => {
      petMotifsRef.current = [];
      const motifCount = 15;
      
      for (let i = 0; i < motifCount; i++) {
        petMotifsRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          rotation: Math.random() * 360,
          scale: Math.random() * 0.5 + 1.0,
          rotationSpeed: (Math.random() - 0.5) * 0.3,
          driftVx: (Math.random() - 0.5) * 0.3,
          driftVy: (Math.random() - 0.5) * 0.3,
          opacity: Math.random() * 0.06 + 0.08,
          pulsePhase: Math.random() * Math.PI * 2
        });
      }
    };

    const initDataFlows = () => {
      dataFlowsRef.current = [];
      const flowCount = 8;
      const types: ('dog' | 'cat' | 'paw')[] = ['dog', 'cat', 'paw'];
      
      for (let i = 0; i < flowCount; i++) {
        dataFlowsRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.6,
          vy: (Math.random() - 0.5) * 0.6,
          opacity: Math.random() * 0.08 + 0.10,
          type: types[Math.floor(Math.random() * types.length)],
          scale: Math.random() * 0.4 + 0.6
        });
      }
    };

    const drawPawPrint = (ctx: CanvasRenderingContext2D, x: number, y: number, scale: number, rotation: number, opacity: number) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(scale, scale);
      
      // Enhanced glowing outline style - no fill, only luminous edges
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 30);
      gradient.addColorStop(0, `rgba(124, 58, 237, ${opacity * 1.2})`);
      gradient.addColorStop(1, `rgba(124, 58, 237, ${opacity * 0.3})`);
      
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 1.8;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Main pad (larger, rounded triangular shape - realistic paw pad)
      ctx.beginPath();
      ctx.moveTo(-8, 12);
      ctx.bezierCurveTo(-12, 8, -12, 2, -8, -2);
      ctx.bezierCurveTo(-4, -4, 4, -4, 8, -2);
      ctx.bezierCurveTo(12, 2, 12, 8, 8, 12);
      ctx.bezierCurveTo(4, 15, -4, 15, -8, 12);
      ctx.closePath();
      ctx.stroke();
      
      // Four toe pads - realistic proportions with slight angular edges for tech feel
      const toes = [
        { x: -10, y: -8, rotation: -15 },  // Left outer toe
        { x: -3, y: -12, rotation: -5 },   // Left inner toe
        { x: 3, y: -12, rotation: 5 },     // Right inner toe
        { x: 10, y: -8, rotation: 15 }     // Right outer toe
      ];
      
      toes.forEach(toe => {
        ctx.save();
        ctx.translate(toe.x, toe.y);
        ctx.rotate((toe.rotation * Math.PI) / 180);
        
        // Toe pad shape - rounded with slight geometric edge
        ctx.beginPath();
        ctx.moveTo(-3, 2);
        ctx.bezierCurveTo(-4, 0, -4, -2, -3, -4);
        ctx.lineTo(3, -4);
        ctx.bezierCurveTo(4, -2, 4, 0, 3, 2);
        ctx.bezierCurveTo(2, 4, -2, 4, -3, 2);
        ctx.closePath();
        ctx.stroke();
        
        // Optional: tiny claw hint (very subtle, angular)
        ctx.beginPath();
        ctx.moveTo(0, -4);
        ctx.lineTo(0, -6);
        ctx.stroke();
        
        ctx.restore();
      });
      
      // Add subtle glow effect around entire paw
      ctx.strokeStyle = `rgba(124, 58, 237, ${opacity * 0.15})`;
      ctx.lineWidth = 3;
      ctx.globalAlpha = 0.3;
      
      // Redraw main pad outline for glow
      ctx.beginPath();
      ctx.moveTo(-8, 12);
      ctx.bezierCurveTo(-12, 8, -12, 2, -8, -2);
      ctx.bezierCurveTo(-4, -4, 4, -4, 8, -2);
      ctx.bezierCurveTo(12, 2, 12, 8, 8, 12);
      ctx.bezierCurveTo(4, 15, -4, 15, -8, 12);
      ctx.closePath();
      ctx.stroke();
      
      ctx.restore();
    };

    const drawDogSilhouette = (ctx: CanvasRenderingContext2D, x: number, y: number, scale: number, opacity: number) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.scale(scale, scale);
      
      // Glowing outline gradient
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 25);
      gradient.addColorStop(0, `rgba(0, 212, 255, ${opacity * 1.2})`);
      gradient.addColorStop(1, `rgba(0, 212, 255, ${opacity * 0.3})`);
      
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 1.8;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Simplified dog profile - geometric with slight curves
      ctx.beginPath();
      // Head and ears
      ctx.moveTo(-18, 8);
      ctx.lineTo(-20, -8);
      ctx.lineTo(-12, -12); // Left ear
      ctx.lineTo(-6, -8);
      // Snout
      ctx.lineTo(0, -14);
      ctx.lineTo(6, -8);
      // Right ear
      ctx.lineTo(12, -12);
      ctx.lineTo(20, -8);
      // Body
      ctx.lineTo(18, 8);
      ctx.bezierCurveTo(14, 12, 6, 14, 0, 14);
      ctx.bezierCurveTo(-6, 14, -14, 12, -18, 8);
      ctx.closePath();
      ctx.stroke();
      
      // Subtle glow
      ctx.strokeStyle = `rgba(0, 212, 255, ${opacity * 0.15})`;
      ctx.lineWidth = 3;
      ctx.globalAlpha = 0.3;
      ctx.stroke();
      
      ctx.restore();
    };

    const drawCatSilhouette = (ctx: CanvasRenderingContext2D, x: number, y: number, scale: number, opacity: number) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.scale(scale, scale);
      
      // Glowing outline gradient
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 25);
      gradient.addColorStop(0, `rgba(252, 211, 77, ${opacity * 1.2})`);
      gradient.addColorStop(1, `rgba(252, 211, 77, ${opacity * 0.3})`);
      
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 1.8;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Simplified cat profile - angular ears, rounded body
      ctx.beginPath();
      // Head with pointed ears
      ctx.moveTo(-16, 4);
      ctx.lineTo(-18, -10); // Left ear (more pointed)
      ctx.lineTo(-8, -6);
      ctx.lineTo(0, -14); // Top of head
      ctx.lineTo(8, -6);
      ctx.lineTo(18, -10); // Right ear
      ctx.lineTo(16, 4);
      // Body
      ctx.bezierCurveTo(12, 10, 6, 12, 0, 12);
      ctx.bezierCurveTo(-6, 12, -12, 10, -16, 4);
      ctx.closePath();
      ctx.stroke();
      
      // Subtle glow
      ctx.strokeStyle = `rgba(252, 211, 77, ${opacity * 0.15})`;
      ctx.lineWidth = 3;
      ctx.globalAlpha = 0.3;
      ctx.stroke();
      
      ctx.restore();
    };

    const drawHolographicGrid = () => {
      const gridSize = 100;
      const time = timeRef.current * 0.0005;
      
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.06)';
      ctx.lineWidth = 1;
      
      for (let x = 0; x < canvas.width; x += gridSize) {
        const wave = Math.sin(x * 0.01 + time) * 3;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        for (let y = 0; y < canvas.height; y += 20) {
          const offset = Math.sin(y * 0.02 + time) * wave;
          ctx.lineTo(x + offset, y);
        }
        ctx.stroke();
      }
      
      ctx.strokeStyle = 'rgba(124, 58, 237, 0.05)';
      for (let y = 0; y < canvas.height; y += gridSize) {
        const wave = Math.sin(y * 0.01 + time) * 3;
        ctx.beginPath();
        ctx.moveTo(0, y);
        for (let x = 0; x < canvas.width; x += 20) {
          const offset = Math.sin(x * 0.02 + time) * wave;
          ctx.lineTo(x, y + offset);
        }
        ctx.stroke();
      }
    };

    const animate = () => {
      timeRef.current++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      drawHolographicGrid();
      
      // Update and draw data flows with improved separation logic
      dataFlowsRef.current.forEach((flow, index) => {
        // Continuous independent drift
        flow.x += flow.vx;
        flow.y += flow.vy;
        
        const dx = mouseRef.current.x - flow.x;
        const dy = mouseRef.current.y - flow.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Cursor interaction: brief attraction then repulsion
        if (distance < 150) {
          const attractionZone = 80;
          const repulsionZone = 150;
          
          if (distance < attractionZone) {
            // Close to cursor: gentle repulsion (scatter outward)
            const repelForce = (attractionZone - distance) / attractionZone * 0.8;
            flow.vx -= (dx / distance) * repelForce;
            flow.vy -= (dy / distance) * repelForce;
          } else {
            // Medium distance: subtle attraction
            const attractForce = (repulsionZone - distance) / repulsionZone * 0.15;
            flow.vx += (dx / distance) * attractForce;
            flow.vy += (dy / distance) * attractForce;
          }
        }
        
        // Separation from other flows (prevent clumping)
        dataFlowsRef.current.forEach((otherFlow, otherIndex) => {
          if (index !== otherIndex) {
            const fdx = flow.x - otherFlow.x;
            const fdy = flow.y - otherFlow.y;
            const fdist = Math.sqrt(fdx * fdx + fdy * fdy);
            
            if (fdist < 100 && fdist > 0) {
              const separationForce = (100 - fdist) / 100 * 0.3;
              flow.vx += (fdx / fdist) * separationForce;
              flow.vy += (fdy / fdist) * separationForce;
            }
          }
        });
        
        // Damping to prevent excessive speed
        flow.vx *= 0.98;
        flow.vy *= 0.98;
        
        // Maintain minimum drift speed
        const speed = Math.sqrt(flow.vx * flow.vx + flow.vy * flow.vy);
        if (speed < 0.2) {
          flow.vx += (Math.random() - 0.5) * 0.1;
          flow.vy += (Math.random() - 0.5) * 0.1;
        }
        
        // Wrap around edges
        if (flow.x < -50) flow.x = canvas.width + 50;
        if (flow.x > canvas.width + 50) flow.x = -50;
        if (flow.y < -50) flow.y = canvas.height + 50;
        if (flow.y > canvas.height + 50) flow.y = -50;
        
        if (flow.type === 'dog') {
          drawDogSilhouette(ctx, flow.x, flow.y, flow.scale, flow.opacity);
        } else if (flow.type === 'cat') {
          drawCatSilhouette(ctx, flow.x, flow.y, flow.scale, flow.opacity);
        } else {
          const pulse = Math.sin(timeRef.current * 0.02) * 0.02 + 1;
          drawPawPrint(ctx, flow.x, flow.y, flow.scale * pulse, timeRef.current * 0.5, flow.opacity);
        }
      });
      
      // Update and draw pet motifs with improved separation
      petMotifsRef.current.forEach((motif, index) => {
        // Continuous independent drift
        motif.x += motif.driftVx;
        motif.y += motif.driftVy;
        motif.rotation += motif.rotationSpeed;
        
        motif.pulsePhase += 0.02;
        const pulse = Math.sin(motif.pulsePhase) * 0.03 + 1;
        
        const dx = mouseRef.current.x - motif.x;
        const dy = mouseRef.current.y - motif.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Cursor interaction with repulsion
        if (distance < 200) {
          const repulsionThreshold = 120;
          
          if (distance < repulsionThreshold) {
            // Repel when too close
            const repelForce = (repulsionThreshold - distance) / repulsionThreshold * 0.5;
            motif.driftVx -= (dx / distance) * repelForce;
            motif.driftVy -= (dy / distance) * repelForce;
          } else {
            // Gentle parallax at medium distance
            const parallax = (200 - distance) / 200 * 0.008;
            motif.x += dx * parallax;
            motif.y += dy * parallax;
          }
        }
        
        // Separation from other motifs
        petMotifsRef.current.forEach((otherMotif, otherIndex) => {
          if (index !== otherIndex) {
            const mdx = motif.x - otherMotif.x;
            const mdy = motif.y - otherMotif.y;
            const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
            
            if (mdist < 120 && mdist > 0) {
              const separationForce = (120 - mdist) / 120 * 0.2;
              motif.driftVx += (mdx / mdist) * separationForce;
              motif.driftVy += (mdy / mdist) * separationForce;
            }
          }
        });
        
        // Damping
        motif.driftVx *= 0.99;
        motif.driftVy *= 0.99;
        
        // Maintain minimum drift
        const speed = Math.sqrt(motif.driftVx * motif.driftVx + motif.driftVy * motif.driftVy);
        if (speed < 0.1) {
          motif.driftVx += (Math.random() - 0.5) * 0.05;
          motif.driftVy += (Math.random() - 0.5) * 0.05;
        }
        
        // Wrap around edges
        if (motif.x < -100) motif.x = canvas.width + 100;
        if (motif.x > canvas.width + 100) motif.x = -100;
        if (motif.y < -100) motif.y = canvas.height + 100;
        if (motif.y > canvas.height + 100) motif.y = -100;
        
        drawPawPrint(ctx, motif.x, motif.y, motif.scale * pulse, motif.rotation, motif.opacity);
      });
      
      // Update and draw particles with improved separation
      particlesRef.current.forEach((particle, index) => {
        particle.pulsePhase += particle.pulseSpeed;
        const pulse = Math.sin(particle.pulsePhase) * 0.5 + 1;
        
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Cursor interaction with ripple and repulsion
        if (distance < 150) {
          const rippleZone = 60;
          
          if (distance < rippleZone) {
            // Strong repulsion when very close
            const repelForce = (rippleZone - distance) / rippleZone * 1.2;
            particle.vx -= (dx / distance) * repelForce;
            particle.vy -= (dy / distance) * repelForce;
          } else {
            // Gentle ripple effect at medium distance
            const ripple = Math.sin(distance * 0.05 - timeRef.current * 0.05);
            const rippleForce = (150 - distance) / 150 * 0.3;
            particle.vx += ripple * (dx / distance) * rippleForce;
            particle.vy += ripple * (dy / distance) * rippleForce;
          }
        }
        
        // Separation from other particles
        particlesRef.current.forEach((otherParticle, otherIndex) => {
          if (index !== otherIndex) {
            const pdx = particle.x - otherParticle.x;
            const pdy = particle.y - otherParticle.y;
            const pdist = Math.sqrt(pdx * pdx + pdy * pdy);
            
            if (pdist < 50 && pdist > 0) {
              const separationForce = (50 - pdist) / 50 * 0.15;
              particle.vx += (pdx / pdist) * separationForce;
              particle.vy += (pdy / pdist) * separationForce;
            }
          }
        });
        
        // Gentle return to base position
        particle.x += (particle.baseX - particle.x) * 0.005;
        particle.y += (particle.baseY - particle.y) * 0.005;
        
        // Apply velocity
        particle.x += particle.vx;
        particle.y += particle.vy;
        
        // Damping
        particle.vx *= 0.97;
        particle.vy *= 0.97;
        
        // Maintain minimum drift
        const speed = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy);
        if (speed < 0.15) {
          particle.vx += (Math.random() - 0.5) * 0.08;
          particle.vy += (Math.random() - 0.5) * 0.08;
        }
        
        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.vx *= -0.8;
          particle.x = Math.max(0, Math.min(canvas.width, particle.x));
        }
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.vy *= -0.8;
          particle.y = Math.max(0, Math.min(canvas.height, particle.y));
        }
        
        // Draw particle with glow
        const glowSize = particle.size * pulse;
        ctx.fillStyle = particle.color;
        ctx.globalAlpha = particle.opacity * 0.3;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, glowSize * 2, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.globalAlpha = particle.opacity;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, glowSize, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw connections with distance check
        particlesRef.current.forEach(otherParticle => {
          const cdx = particle.x - otherParticle.x;
          const cdy = particle.y - otherParticle.y;
          const cdist = Math.sqrt(cdx * cdx + cdy * cdy);
          
          if (cdist < 140 && cdist > 30) {
            ctx.strokeStyle = particle.color;
            ctx.globalAlpha = (1 - cdist / 140) * particle.opacity * 0.4;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
          }
        });
      });
      
      ctx.globalAlpha = 1;
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, #FDFDFD 40%, #FAFAFC 100%)' }}
    />
  );
}
